<template>
  <div
    class="group absolute z-10 w-32 -translate-x-1/2 -translate-y-1/2 text-center"
    :style="positionStyle"
    :data-map-objective="objective.key"
  >
    <button
      type="button"
      class="inline-flex size-7 items-center justify-center rounded-full border bg-bg-elevated/95 text-sm shadow-sm transition-[border-color,background-color] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      :class="[
        markerColorClass,
        editable && movable ? 'cursor-move touch-none select-none hover:bg-bg-surface' : editable ? 'cursor-pointer hover:bg-bg-surface' : 'cursor-default hover:bg-bg-surface',
      ]"
      :aria-disabled="!editable"
      :aria-label="markerAriaLabel"
      @pointerdown="onPointerDown"
      @click="onClick"
      @keydown="onKeydown"
    >
      <span aria-hidden="true" class="leading-none">{{ markerIcon }}</span>
    </button>
    <p v-if="markerLabel !== null" class="mt-1 text-2xs font-bold uppercase tracking-wide" :class="markerTextClass">{{ markerLabel }}</p>
    <GvgMapPointPopover
      :label="objective.label"
      :player-names="playerNames"
      :placement="popoverPlacement"
      :movable="editable && movable"
      :action-label="actionLabel"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import type { MapTagObjective } from '../../types'
import type { MapMarkerNudge } from './GvgMapGroupMarker.vue'
import GvgMapPointPopover from './GvgMapPointPopover.vue'
import { gvgMapEn } from './locales/en.ts'
import { gvgMapRu } from './locales/ru.ts'

export interface GvgMapTagMarkerProps {
  readonly objective: MapTagObjective
  readonly playerNames: ReadonlyArray<string>
  readonly editable: boolean
  readonly kind?: 'forest' | 'tree' | 'boss'
  readonly movable?: boolean
}

const props = withDefaults(defineProps<GvgMapTagMarkerProps>(), {
  kind: 'forest',
  movable: false,
})

const emit = defineEmits<{
  (event: 'move-start', pointerEvent: PointerEvent): void
  (event: 'nudge', value: MapMarkerNudge): void
  (event: 'edit'): void
}>()

const messages = useLocaleMessages(defineLocaleCatalog({ en: gvgMapEn, ru: gvgMapRu }))

const positionStyle = computed<Readonly<Record<string, string>>>(() => ({
  left: `${props.objective.xBasisPoints / 100}%`,
  top: `${props.objective.yBasisPoints / 100}%`,
}))

const markerIcon = computed<string>(() => {
  if (props.kind === 'tree') return '🌴'
  if (props.kind === 'boss') return '♛'
  return '🌲'
})
const markerLabel = computed<string | null>(() => props.kind === 'forest' ? null : props.objective.label)
const markerColorClass = computed<string>(() => {
  if (props.kind === 'tree') return 'border-accent text-accent'
  if (props.kind === 'boss') return 'border-warning text-warning'
  return 'border-success text-success'
})
const markerTextClass = computed<string>(() => props.kind === 'boss' ? 'text-warning' : 'text-accent')
const popoverPlacement = computed<'above' | 'below'>(() =>
  props.objective.yBasisPoints > 6500 ? 'above' : 'below',
)
const markerAriaLabel = computed<string>(() => {
  const playerCount = props.playerNames.length
  const editGuidance = props.editable
    ? (props.movable ? messages.value.tagMarker.editAndMovePrompt : messages.value.tagMarker.editPlayersPrompt)
    : ''
  return `${messages.value.tagMarker.rosterLabel(props.objective.label, playerCount)}${editGuidance}`
})
const actionLabel = computed<string | null>(() => {
  if (!props.editable) return null
  return props.movable ? messages.value.tagMarker.movableAction : messages.value.tagMarker.staticAction
})

function onPointerDown(event: PointerEvent): void {
  if (!props.editable || !props.movable || event.button !== 0) return
  event.preventDefault()
  emit('move-start', event)
}

function onClick(event: MouseEvent): void {
  if (!props.editable) return
  if (props.movable && event.detail !== 0) return
  emit('edit')
}

function onKeydown(event: KeyboardEvent): void {
  if (!props.editable || !props.movable) return
  const step = event.shiftKey ? 500 : 100
  if (event.key === 'ArrowLeft') emit('nudge', { xDeltaBasisPoints: -step, yDeltaBasisPoints: 0 })
  else if (event.key === 'ArrowRight') emit('nudge', { xDeltaBasisPoints: step, yDeltaBasisPoints: 0 })
  else if (event.key === 'ArrowUp') emit('nudge', { xDeltaBasisPoints: 0, yDeltaBasisPoints: -step })
  else if (event.key === 'ArrowDown') emit('nudge', { xDeltaBasisPoints: 0, yDeltaBasisPoints: step })
  else return
  event.preventDefault()
}
</script>